import {
  EventMembers,
  EventTotalGuests,
  GetWidgetDataResponse,
  PagesTypeType,
  WidgetDates as ServerlessWidgetDates,
} from '@wix/ambassador-events-ui-v1-widget-data/types'
import {Event as Event_V3} from '@wix/ambassador-events-v3-event/types'
import {Category} from '@wix/ambassador-wix-events-web/types'
import {
  CalendarDates,
  CalendarDayV2,
  EventsDates,
  WIDGET_TYPE,
  WidgetData,
  WidgetDates,
} from '@wix/wix-events-commons-statics'
import {DETAILS_ROUTE} from '../../../commons/constants/navigation'
import {PagesType} from '../../../commons/enums'
import {DemoEvents, SiteSettings} from '../../../commons/types/state'

export interface UnpackedWidgetData {
  events: wix.events.Event[] | Event_V3[]
  featuredEvent?: wix.events.Event
  members: Record<string, EventMembers>
  membershipOffers: Record<string, boolean>
  totalGuests: Record<string, EventTotalGuests>
  hasMoreEvents: boolean
  categories: Category[]
  demoEvents: DemoEvents
  siteSettings: SiteSettings
  dates: WidgetDates
}

export const unpackWidgetData = (
  widgetData: WidgetData | GetWidgetDataResponse,
  useServerless: boolean,
): UnpackedWidgetData =>
  useServerless
    ? unpackServerlessWidgetData(widgetData as GetWidgetDataResponse)
    : unpackMiddlewareWidgetData(widgetData as WidgetData)

const unpackMiddlewareWidgetData = (widgetData: WidgetData): UnpackedWidgetData => {
  const {component, hasMoreEvents, categories, demoEvents: widgetDataDemoEvents, siteSettings, dates} = widgetData
  const {events, featuredEvent} = component

  const demoEvents: DemoEvents = {
    ...widgetDataDemoEvents,
    [WIDGET_TYPE.FEATURED]: widgetDataDemoEvents?.[WIDGET_TYPE.LIST],
  }

  return {
    events,
    featuredEvent,
    members: {}, // Comes from ExtendedEvent in middleware approach
    membershipOffers: {}, // Comes from ExtendedEvent in middleware approach
    totalGuests: {}, // Comes from ExtendedEvent in middleware approach
    hasMoreEvents,
    categories,
    demoEvents,
    siteSettings,
    dates,
  }
}

const mapDates = (serverlessDates: ServerlessWidgetDates): WidgetDates => {
  const events: EventsDates = {}

  Object.entries(serverlessDates.events ?? {}).forEach(
    ([
      key,
      {
        utcOffset,
        monthDay,
        weekDay,
        month,
        fullDate,
        shortStartDate,
        startDate,
        daysLeft,
        startTime,
        day,
        year,
        yearMonth,
        formattedRegistrationStartDate,
        formattedRegistrationEndDate,
        startDateIsoFormatNotUtc,
        endDateIsoFormatNotUtc,
        multiDayInfo,
      },
    ]) => {
      events[key] = {
        utcOffset,
        monthDay,
        weekDay,
        month,
        fullDate,
        shortStartDate,
        startDate,
        daysLeft,
        startTime,
        day,
        year,
        yearMonth,
        formattedRegistrationStartDate,
        formattedRegistrationEndDate,
        startDateISOFormatNotUTC: startDateIsoFormatNotUtc,
        endDateISOFormatNotUTC: endDateIsoFormatNotUtc,
        multiDayInfo,
      }
    },
  )

  const month: Dictionary<{next: string; previous: string; fullMonthText: string}> = {}

  if (serverlessDates.calendar.month) {
    Object.entries(serverlessDates.calendar.month).forEach(([key, value]) => {
      month[key] = {
        next: value.next!,
        previous: value.previous!,
        fullMonthText: value.fullMonthText!,
      }
    })
  }

  const calendarEvents: Dictionary<string[]> = {}

  if (serverlessDates.calendar.events) {
    Object.entries(serverlessDates.calendar.events).forEach(([key, value]) => {
      calendarEvents[key] = value.events
    })
  }

  const calendarDemoEvents: Dictionary<string[]> = {}

  if (serverlessDates.calendar.demoEvents) {
    Object.entries(serverlessDates.calendar.demoEvents).forEach(([key, value]) => {
      calendarDemoEvents[key] = value.events
    })
  }

  const days: Dictionary<CalendarDayV2> = {}

  if (serverlessDates.calendar.days) {
    Object.entries(serverlessDates.calendar.days).forEach(([key, value]) => {
      days[key] = {
        dayMonthText: value.dayMonthText!,
        weekDay: value.weekDay!,
        month: value.month!,
        dayNumber: value.dayNumber!,
      }
    })
  }

  const calendar: CalendarDates = {
    referenceDate: serverlessDates.calendar.referenceDate,
    today: serverlessDates.calendar.today,
    month,
    weekDayNames: serverlessDates.calendar.weekDayNames,
    events: calendarEvents,
    demoEvents: calendarDemoEvents,
    days,
  }

  const dates: WidgetDates = {events, calendar}

  return dates
}

const unpackServerlessWidgetData = (widgetData: GetWidgetDataResponse): UnpackedWidgetData => {
  const events = widgetData.events as unknown as Event_V3[]
  const categories = widgetData.categories as unknown as Category[]

  const {
    members,
    membershipOffers,
    totalGuests,
    hasMoreEvents,
    siteSettings: widgetSiteSettings,
    dates: widgetDates,
  } = widgetData

  const {detailsPagePath, firstTimeCompleted, pagesType} = widgetSiteSettings

  const pagesTypeMap = {
    [PagesTypeType.ENABLED]: PagesType.DETAILS_PAGE_ENABLED,
    [PagesTypeType.DISABLED]: PagesType.DETAILS_PAGE_DISABLED,
  }

  const siteSettings: SiteSettings = {
    settings: {
      firstTimeCompleted,
      pagesType: pagesTypeMap[pagesType],
      detailsPagePath: detailsPagePath as DETAILS_ROUTE,
    },
  }

  const demoEvents: DemoEvents = {
    [WIDGET_TYPE.SINGLE]: widgetData.singleDemoEvents as unknown as Event_V3[],
    [WIDGET_TYPE.LIST]: widgetData.listDemoEvents as unknown as Event_V3[],
    [WIDGET_TYPE.FEATURED]: widgetData.listDemoEvents as unknown as Event_V3[],
  }

  const dates = mapDates(widgetDates)

  return {events, members, membershipOffers, totalGuests, hasMoreEvents, categories, demoEvents, siteSettings, dates}
}
